import React, { useState, useEffect, useMemo } from 'react';
import {
  Typography,
  Drawer,
  Input,
  Select,
  Flex,
  Button,
  Card,
  Space,
  Skeleton,
  Checkbox,
  Col,
  Row,
  Switch,
} from 'antd';
import { useSelector } from 'react-redux';
import grades from 'util/grades';
import CreatableSelect from 'react-select/creatable';
import { _isSuperadmin } from 'util/auth';
import { useUpdateLead } from 'components/pages/crm-spot/hooks/useUpdateLead';
import { MessageOutlined, EditOutlined, MailOutlined } from '@ant-design/icons';
import { formatDate } from 'components/pages/crm-spot/utils/format-date';
import { useLeadSources } from 'components/pages/crm-spot/hooks/useLeadSources';
import { useLeadSubStages } from 'components/pages/crm-spot/hooks/useLeadSubStages';
import { Card as ScholaCard } from 'components/card/card';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import 'react-datepicker/dist/react-datepicker.css';
import GooglePlaces from 'components/google-places-field/google-places-field';

const { Title, Paragraph, Text } = Typography;

// Helper to ensure uuid in all options
const gradesWithUuid = grades.map((g) => ({
  ...g,
  uuid: g.value || g.label || Math.random().toString(36).substr(2, 9),
}));
const languageOptions = [
  { value: 'english', label: 'English', uuid: 'english' },
  { value: 'spanish', label: 'Spanish', uuid: 'spanish' },
];

export const LeadEdition = ({ notesHook, open, onClose, getLeadAssignments }) => {
  const { selectedLead } = useSelector((state) => state.spot);

  // Local state for the form
  const [formState, setFormState] = useState(() => ({
    parent_first_name: '',
    parent_last_name: '',
    child_first_name: '',
    child_last_name: '',
    email: '',
    phone: '',
    language: '',
    grade: '',
    address: '',
    city: '',
    state: '',
    zipcode: '',
    lead_status_id: '',
    application_received: false,
    application_valid: false,
    application_received_at: '',
    enrollment_confirmed: true,
    enrollment_confirmed_at: '',
  }));

  // State for the note input
  const [noteText, setNoteText] = useState('');

  const { fullNotes, createNode, loading } = notesHook;

  // Update formState when selectedLead changes
  useEffect(() => {
    setFormState({
      parent_first_name: selectedLead?.parent_first_name || '',
      parent_last_name: selectedLead?.parent_last_name || '',
      child_first_name: selectedLead?.child_first_name || '',
      child_last_name: selectedLead?.child_last_name || '',
      email: selectedLead?.email || '',
      phone: selectedLead?.phone || '',
      language: selectedLead?.language || 'english',
      lead_source_id: selectedLead?.lead_source_id || null,
      lead_status_id: selectedLead?.lead_status_id || null,
      grade: selectedLead?.grade || '',
      address_description: selectedLead?.address_description || '',
      city: selectedLead?.city || '',
      state: selectedLead?.state || '',
      zipcode: selectedLead?.zipcode || '',
      application_valid: selectedLead?.application_valid || undefined,
      application_received: selectedLead?.application_received || undefined,
      application_received_at: selectedLead?.application_received_at || null,
      enrollment_confirmed: selectedLead?.enrollment_confirmed || undefined,
      enrollment_confirmed_at: selectedLead?.enrollment_confirmed_at || null,
    });
  }, [selectedLead]);

  /* console.log({ selectedLead }); */

  // Set readonly for Lead Source if needed
  useEffect(() => {
    const readonly =
      formState.lead_source_id < 0 &&
      selectedLead?.created_on !== 'admin' &&
      selectedLead?.created_on !== 'admin-reset' &&
      !_isSuperadmin();

    setLeadSourceReadonly(!!readonly);
  }, [formState.lead_source_id, selectedLead?.created_on]);

  const handleInputChange = (id, value) => {
    setFormState((prev) => ({ ...prev, [id]: value }));
  };

  const [isEditing, setIsEditing] = useState(false);

  const { updateLead } = useUpdateLead();
  const handleUpdate = async () => {
    const schoolId = selectedLead.school_id;
    const leadIds = [selectedLead.id];

    try {
      await updateLead({ schoolId, leadIds, values: formState });
      await getLeadAssignments();
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating lead:', error);
    }
  };

  const handleEdit = () => {
    if (isEditing) {
      handleUpdate();
    } else {
      setIsEditing(true);
    }
  };

  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    await createNode({ leadId: selectedLead.id, note: noteText });
    setNoteText('');
  };

  const [leadSourceReadonly, setLeadSourceReadonly] = useState(false);

  const { sources, fetchSources, addLeadSource } = useLeadSources(selectedLead?.school_id);

  // Lead Source select (uses sources directly, no local state)
  const renderSelectLeadSource = () => {
    // Use sources directly, sorted and mapped
    const options = (sources || [])
      .sort((a, b) => (a.name > b.name ? 1 : -1))
      .map((ls) => ({ value: ls.id, label: ls.name, uuid: ls.id }));

    let auxPlaceholderLead = '';
    const selectedOption = options.find((status) => status.value === formState.lead_source_id);
    if (selectedOption) {
      auxPlaceholderLead = selectedOption.label;
    }

    const onChange = (selected) => {
      if (!selected) return;
      setFormState((prev) => ({
        ...prev,
        lead_source_id: selected.value,
      }));
    };

    const onCreateOpt = async (value) => {
      const leadSource = await addLeadSource(value);
      if (leadSource) {
        await fetchSources();
        setFormState((prev) => ({ ...prev, lead_source_id: leadSource.id }));
      }
    };

    return (
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <label htmlFor="source">Lead Source</label>
        {!leadSourceReadonly && (
          <CreatableSelect
            isClearable
            closeOnSelect={false}
            className="lead-select"
            classNamePrefix="schola"
            onChange={onChange}
            onCreateOption={onCreateOpt}
            placeholder={auxPlaceholderLead || 'Select or create a source'}
            options={options}
            isDisabled={!isEditing}
            value={options.find((opt) => opt.value === formState.lead_source_id) || null}
            uuid="lead_source_id"
          />
        )}
        {leadSourceReadonly && <label className="ml-2">{auxPlaceholderLead}</label>}
      </div>
    );
  };
  // Sub stages (statuses)
  const { statuses, fetchStatuses, addLeadStatus } = useLeadSubStages(selectedLead?.school_id);
  const renderSelectLeadStages = () => {
    let options = [];
    statuses &&
      statuses
        .sort((a, b) => (a.name > b.name ? 1 : -1))
        .forEach((ls) => {
          options.push({ value: ls.id, label: ls.name, uuid: ls.id });
        });

    let auxPlaceholderLead;
    try {
      auxPlaceholderLead = options.filter((status) => {
        return status.value === formState.lead_status_id;
      })[0]?.label;
    } catch (error) {}

    const onChange = (selected) => {
      if (!selected) {
        handleInputChange('lead_status_id', '');
        return;
      }
      handleInputChange('lead_status_id', selected.value);
    };

    // Create status, refresh and select
    const onCreateOpt = async (name) => {
      const leadStatus = await addLeadStatus(name);
      if (leadStatus) {
        await fetchStatuses();
        setFormState((prev) => ({ ...prev, lead_status_id: leadStatus.id }));
      }
    };

    return (
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <label htmlFor="lead_sub_stages">Sub Stage</label>
        <CreatableSelect
          isClearable
          closeOnSelect={false}
          className="lead-select"
          classNamePrefix="schola"
          onChange={onChange}
          onCreateOption={onCreateOpt}
          value={options.find((opt) => opt.value === formState.lead_status_id) || null}
          options={options}
          placeholder={auxPlaceholderLead}
          isDisabled={!isEditing}
          uuid="lead_status_id"
        />
      </div>
    );
  };
  const setLocationFields = (locationInfo) => {
    console.log(locationInfo);
    setFormState((prev) => ({
      ...prev,
      address: locationInfo?.data?.description || locationInfo?.address_description || '',
      address_description: locationInfo?.data?.description || locationInfo?.address_description || '',
      state: locationInfo?.state || '',
      city: locationInfo?.city || '',
      zipcode: locationInfo?.zip || '',
      longitude: locationInfo?.longitude || '',
      latitude: locationInfo?.latitude || '',
    }));
  };
  const clearLocationFields = () => {
    setFormState((prev) => ({
      ...prev,
      address: '',
      state: '',
      city: '',
      zipcode: '',
      address_description: '',
      longitude: '',
      latitude: '',
    }));
  };

  return (
    <Drawer
      open={open}
      size="large"
      placement="right"
      onClose={onClose}
      title={
        <Flex align="center" justify="space-between">
          <Title level={5} style={{ marginBottom: 0 }}>
            Lead Edition
          </Title>
          <Button onClick={handleEdit}>{isEditing ? 'Save' : 'Edit Lead'}</Button>
        </Flex>
      }>
      <div>
        <Card
          title={
            <Title level={5} style={{ margin: 0 }}>
              Lead Information
            </Title>
          }>
          <Paragraph style={{ fontSize: '0.85rem' }}>Parent Information</Paragraph>
          <Flex align="center" gap={16}>
            <InputField
              id="parent_first_name"
              label="Parent First Name"
              value={formState.parent_first_name}
              onChange={(e) => handleInputChange('parent_first_name', e.target.value)}
              disabled={!isEditing}
            />
            <InputField
              id="parent_last_name"
              label="Parent Last Name"
              value={formState.parent_last_name}
              onChange={(e) => handleInputChange('parent_last_name', e.target.value)}
              disabled={!isEditing}
            />
          </Flex>
          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <label className="font-12 font-500 label-address">Address</label>
              <GooglePlaces
                aria-label="address"
                initialValue={formState.address_description}
                inputValue={formState.address_description}
                onSelect={setLocationFields}
                onChange={clearLocationFields}
                disabled={!isEditing}
                skipSuggest={(suggest) => {
                  return suggest.types.indexOf('geocode') === -1;
                }}
              />
            </Col>
            <Col xs={24} md={12}>
              <InputField
                id="city"
                label="City"
                value={formState.city}
                onChange={(e) => handleInputChange('city', e.target.value)}
                disabled={!isEditing}
              />
            </Col>
          </Row>
          <Flex align="center" gap={16}>
            <InputField
              id="state"
              label="State"
              value={formState.state}
              onChange={(e) => handleInputChange('state', e.target.value)}
              disabled={!isEditing}
            />
            <InputField
              id="zipcode"
              label="Zipcode"
              value={formState.zipcode}
              onChange={(e) => handleInputChange('zipcode', e.target.value)}
              disabled={!isEditing}
            />
          </Flex>
          <Flex align="center" gap={16}>
            <InputField
              id="email"
              label="Email"
              value={formState.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              disabled={!isEditing}
            />
            <InputField
              id="phone"
              label="Phone"
              value={formState.phone}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              disabled={!isEditing}
            />
          </Flex>
          <Flex align="center" gap={16}>
            <InputField id="language" label="Language">
              <Select
                style={{ width: '100%' }}
                value={formState.language}
                onChange={(value) => handleInputChange('language', value)}
                options={languageOptions}
                disabled={!isEditing}
                uuid="language"
              />
            </InputField>
          </Flex>

          <Paragraph style={{ fontSize: '0.85rem', marginTop: '24px' }}>Student Information</Paragraph>
          <Flex align="center" gap={16}>
            <InputField
              id="child_first_name"
              label="Student First Name"
              value={formState.child_first_name}
              onChange={(e) => handleInputChange('child_first_name', e.target.value)}
              disabled={!isEditing}
            />
            <InputField
              id="child_last_name"
              label="Student Last Name"
              value={formState.child_last_name}
              onChange={(e) => handleInputChange('child_last_name', e.target.value)}
              disabled={!isEditing}
            />
          </Flex>
          <Flex align="center" style={{ width: '50%', paddingRight: '0.5rem' }}>
            <InputField id="grade" label="Grade">
              <Select
                style={{ width: '100%' }}
                value={formState.grade}
                onChange={(value) => handleInputChange('grade', value)}
                options={gradesWithUuid}
                disabled={!isEditing}
                uuid="grade"
              />
            </InputField>
          </Flex>
          <Paragraph style={{ fontSize: '0.85rem', marginTop: '24px' }}>Lead</Paragraph>
          <Row gutter={[16, 16]} className="spacing-mb-16">
            <Col xs={24} md={12}>
              <ScholaCard className="spacing-p-8">
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <label className="spacing-mb-8">Application Received</label>
                  <Flex align="center" gap={8}>
                    <Checkbox
                      id="application_received"
                      checked={formState.application_received}
                      onChange={(e) => handleInputChange('application_received', e.target.checked)}
                      disabled={!isEditing}
                    />
                    <label className="form-check-label" htmlFor="application_received">
                      Received
                    </label>
                  </Flex>
                </div>
                {formState.application_received && (
                  <>
                    <div className="form-group radio-buttons spacing-mt-8">
                      <div
                        className="form-check pl-0 toggle-lead-valid"
                        style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span>Invalid</span>
                        <Switch
                          checked={formState.application_valid === true}
                          disabled={!isEditing}
                          onChange={() =>
                            handleInputChange(
                              'application_valid',
                              formState.application_valid === undefined ? true : !formState.application_valid
                            )
                          }
                          id={'rm'}
                          className="rm-toggle"
                        />
                        <span>Valid</span>
                      </div>
                    </div>
                    <div className="lead-information-datepiker">
                      <label>Received On</label>
                      <DatePicker
                        ref={(c) => c}
                        className="lead-date-picker"
                        disabled={!isEditing}
                        selected={formState.application_received_at ? moment(formState.application_received_at) : null}
                        onChange={(value) => {
                          handleInputChange('application_received_at', value ? value.toISOString() : '');
                        }}
                        maxDate={moment()}
                        dateFormat="MM/DD/yyyy"
                        todayButton="Today"
                      />
                    </div>
                  </>
                )}
              </ScholaCard>
            </Col>
            <Col xs={24} md={12}>
              <ScholaCard className="spacing-p-8">
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <label className="spacing-mb-8">Enrollment Confirmed</label>
                  <Flex align="center" gap={8}>
                    <Checkbox
                      id="enrollment_confirmed"
                      checked={formState.enrollment_confirmed}
                      onChange={(e) => handleInputChange('enrollment_confirmed', e.target.checked)}
                      disabled={!isEditing}
                    />
                    <label className="form-check-label" htmlFor="enrollment_confirmed">
                      Confirmed
                    </label>
                  </Flex>
                </div>
                {formState.enrollment_confirmed && (
                  <>
                    <div className="lead-information-datepiker spacing-mt-16">
                      <label>Confirmed On</label>
                      <DatePicker
                        ref={(c) => c}
                        className="lead-date-picker"
                        disabled={!isEditing}
                        selected={formState.enrollment_confirmed_at ? moment(formState.enrollment_confirmed_at) : null}
                        onChange={(value) => {
                          handleInputChange('enrollment_confirmed_at', value ? value.toISOString() : '');
                        }}
                        maxDate={moment()}
                        dateFormat="MM/DD/yyyy"
                        todayButton="Today"
                      />
                    </div>
                  </>
                )}
              </ScholaCard>
            </Col>
          </Row>
          <Row gutter={[16, 16]} className="spacing-mb-16">
            <Col xs={24} md={12}>
              {renderSelectLeadStages()}
            </Col>
            <Col xs={24} md={12}>
              {renderSelectLeadSource()}
            </Col>
          </Row>
        </Card>

        {/* Add Note Section */}
        <div style={{ marginTop: '24px' }}>
          <Card
            title={
              <Title level={5} style={{ margin: 0 }}>
                Add Note
              </Title>
            }
            style={{ borderRadius: '8px' }}>
            <Input.TextArea
              placeholder="Add your notes here..."
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              style={{ marginBottom: '16px' }}
            />
            <Button type="primary" loading={loading} onClick={handleAddNote}>
              Add Note
            </Button>
          </Card>
        </div>

        {/* Interaction History Section */}
        <div style={{ marginTop: '24px' }}>
          <Card
            title={
              <Title level={5} style={{ margin: 0 }}>
                Interaction History
              </Title>
            }
            style={{ borderRadius: '8px' }}>
            <Space direction="vertical" style={{ width: '100%' }} size="large">
              {loading ? (
                <Skeleton />
              ) : fullNotes?.length > 0 ? (
                fullNotes?.map((note) => <NoteCard key={note.id} note={note} />)
              ) : (
                <Text>No Interaction Histoy Yet.</Text>
              )}
            </Space>
          </Card>
        </div>
      </div>
    </Drawer>
  );
};

const InputField = ({ children, id, label, value, disabled, onChange }) => {
  return (
    <label htmlFor={id} style={{ display: 'block', width: '100%', marginBottom: '8px' }}>
      <Text>{label}</Text>
      {children || <Input id={id} name={id} value={value} onChange={onChange} disabled={disabled} />}
    </label>
  );
};

export const NoteCard = ({ note }) => {
  return (
    <div
      style={{
        display: 'flex',
        gap: '12px',
        background: '#f5f5f5',
        padding: '16px',
        borderRadius: '8px',
      }}>
      {!note.object_type ? (
        <EditOutlined style={{ color: '#006b8f' }} />
      ) : note?.type === 'sms' ? (
        <MessageOutlined />
      ) : (
        <MailOutlined />
      )}
      <div style={{ flex: 1 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
          <Text strong>{note.user_id || `Campaign: ${note.campaign}`}</Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {formatDate(note.created_at)}
          </Text>
        </div>
        <Text>{note.note || `Sent a ${note.type}`}</Text>
      </div>
    </div>
  );
};
